// "Questions? <lead>" from the firm header: ask the Customer Experience Manager without leaving the page.
// Posts to /api/intake/messages (the lead is emailed) and shows the thread from the status endpoint.
import { useEffect, useRef, useState } from 'react';
import type { Lead, MessageView, StatusPage } from '@upm/domain';
import { api } from '../lib/api';
import { firstName, fmtMonthDay, fmtTime } from '../lib/format';
import { Banner, Button, Dialog, Spinner, TextArea, cx } from '../ui';

export function HelpDialog({ open, lead, onClose, onSent }: {
  open: boolean; lead: Lead; onClose: () => void; onSent?: (m: MessageView) => void;
}) {
  const [thread, setThread] = useState<MessageView[] | null>(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lf = firstName(lead.name, 'us');
  const list = useRef<HTMLUListElement>(null);
  // Keep the newest message in view.
  useEffect(() => { const el = list.current; if (el) el.scrollTop = el.scrollHeight; }, [thread]);

  useEffect(() => {
    if (!open) return;
    setSent(false);
    setError(null);
    setThread(null);
    let cancelled = false;
    api.get<StatusPage>('/api/intake/status').then((s) => { if (!cancelled) setThread(s.messages); }, () => { if (!cancelled) setThread([]); });
    return () => { cancelled = true; };
  }, [open]);

  const send = async () => {
    const text = body.trim();
    if (!text) { setError('Type your question first.'); return; }
    setBusy(true);
    setError(null);
    try {
      const m = await api.post<MessageView>('/api/intake/messages', { body: text });
      setThread((t) => [...(t ?? []), m]);
      setBody('');
      setSent(true);
      onSent?.(m);
    } catch {
      setError('We couldn’t send your question. Try again, or email instead.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={`Ask ${lf} a question`}
      onClose={onClose}
      footer={sent ? <>
        <Button onClick={() => setSent(false)}>Ask something else</Button>
        <Button variant="primary" onClick={onClose}>Done</Button>
      </> : <>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={() => void send()} disabled={busy}>{busy ? 'Sending…' : 'Send question'}</Button>
      </>}
    >
      <p className="m-0 text-ink-2">
        {lead.name} is your {lead.title}. Ask anything about the questionnaire, your files or the move itself.
      </p>
      {thread === null && <Spinner label="Loading your messages" />}
      {thread && thread.length > 0 && (
        <ul ref={list} aria-label="Your conversation" className="m-0 flex max-h-[240px] list-none flex-col gap-2.5 overflow-y-auto p-0">
          {thread.map((m) => (
            <li key={m.id} className={cx('rounded-xl px-3 py-2 text-sm', m.from === 'staff' ? 'bg-brand-tint' : 'bg-subtle')}>
              <div className="text-[13px] font-semibold">{m.authorName} <span className="font-normal text-muted">· {fmtMonthDay(m.at)}, {fmtTime(m.at)}</span></div>
              <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.body}</div>
            </li>
          ))}
        </ul>
      )}
      {sent && <Banner tone="success" role="status">Sent. {lead.name} will reply by email and on your status page.</Banner>}
      {!sent && <TextArea label="Your question" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10_000} />}
      {error && <div role="alert" className="text-sm font-semibold text-bad">{error}</div>}
      <p className="m-0 text-sm text-muted">Or email <a href={`mailto:${lead.email}`}>{lead.email}</a></p>
    </Dialog>
  );
}
